import { Fragment } from 'react';

import type { OrganizationRecord } from '@web/data/repositories';
import { t } from '@web/i18n';
import { AppIcons, Card, Switch } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';

interface JitBannerProps {
  org: OrganizationRecord;
  onToggle: () => void;
}

export function JitBanner({ org, onToggle }: Readonly<JitBannerProps>): React.JSX.Element {
  const enabled = org.jitProvisioning.enabled;
  return (
    <div className="mb-4.5">
      <Card tone={enabled ? 'neutral' : 'amber'}>
        <div
          className={twcn(
            'grid grid-cols-[auto_1fr_auto] items-center gap-3.5 px-4 py-3.5',
            !enabled && 'bg-tone-amber-tint rounded-lg',
          )}
        >
          <div
            className={twcn(
              'inline-flex h-row-h w-row-h items-center justify-center rounded-md text-section-meta',
              enabled ? 'bg-primary-tint text-primary' : 'bg-tone-amber-tint text-tone-amber-ink',
            )}
            aria-hidden
          >
            <AppIcons name={enabled ? 'lightning' : 'lightning-slash'} size={15} />
          </div>

          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2 text-base font-medium text-foreground">
              {t('members.jit.titleOn')}
              <span
                className={twcn(
                  'inline-flex h-5 items-center rounded-pill px-1.5 text-card-id font-medium',
                  enabled
                    ? 'bg-tone-green-tint text-tone-green-ink'
                    : 'bg-tone-amber-tint text-tone-amber-ink',
                )}
              >
                {enabled ? t('members.jit.statusOn') : t('members.jit.statusOff')}
              </span>
            </div>
            <div className="text-meta text-muted-foreground">
              {enabled ? (
                <>
                  {t('members.jit.subOnBefore')}
                  {org.allowedDomains.map((d, i) => (
                    <Fragment key={d}>
                      <span className="font-mono text-foreground">{d}</span>
                      {i < org.allowedDomains.length - 1 ? ', ' : ''}
                    </Fragment>
                  ))}
                  {t('members.jit.subOnAfter')}
                </>
              ) : (
                t('members.jit.subOff')
              )}
            </div>
          </div>

          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            aria-label={enabled ? t('members.jit.toggleOffTitle') : t('members.jit.toggleOnTitle')}
            title={enabled ? t('members.jit.toggleOffTitle') : t('members.jit.toggleOnTitle')}
          />
        </div>
      </Card>
    </div>
  );
}
